import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { X, Clock, Paperclip, Send, Download, FileText, Settings, MessageSquare, Trash2, Columns } from '../../icons';
import {
  patchTask, deleteTask,
  listTaskComments, createTaskComment, deleteTaskComment,
  listTaskAttachments, addTaskAttachment, deleteTaskAttachment,
  TASK_STATUSES, TASK_STATUS_META, TASK_PRIORITIES, TASK_PRIORITY_META,
  type TaskDto, type TaskStatus, type TaskPriority,
  type TaskCommentDto, type TaskAttachmentDto,
} from '../../api/tasks';
import { uploadChatAttachment, requestPresignedDownload } from '../../api/files';
import { listTeamMembers, type TeamMemberDto } from '../../api/project-teams';
// 2026-08-21 — 목록보기 표의 ＋ 로 만든 커스텀 필드를 상세에서도 채운다(같은 값·같은 편집기).
import {
  listCustomFields, setCustomFieldValue,
  type CustomFieldDto, type CustomFieldValue,
} from '../../api/task-extras';
import { FieldCell } from './custom-field-ui';
import { useAuthStore } from '../../store/authStore';
import { stripColor } from './ProjectTopBar';
import { AttachmentTypeIcon } from './AttachmentTypeIcon';
import { Combobox, TaskStatusIcon, AutoGrowTextarea } from '../../components/shared';
import { layerConfirm, layerAlert } from '../../components/LayerPopup/LayerPopup';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';

interface TaskDetailModalProps {
  task: TaskDto;
  /** v2.9.17 — 담당자 후보(팀원만). 프로젝트의 소속 팀 id, 무소속(개인)=null. 미전달 시 기존 동작 무변경. */
  teamId?: string | null;
  onClose: () => void;
  /** 저장 성공 — caller 가 목록 반영. */
  onSaved: (updated: TaskDto) => void;
  /** 삭제 성공 — caller 가 목록 재조회. */
  onDeleted: () => void;
  /**
   * 추가 정보(커스텀 필드) 값이 바뀌었다 (2026-08-21).
   *
   * 커스텀 필드 값은 TaskDto 에 실려 오지 않아 `onSaved` 로는 표에 전해지지 않는다.
   * 목록보기 표(TaskTableView)가 자기 값을 다시 읽게 하려고 따로 알린다.
   * 저장 버튼과 무관하게 **고치는 즉시** 서버에 반영되므로 그 순간 부른다.
   */
  onCustomFieldsChanged?: () => void;
  isLeaving?: boolean;
}

function localDateStr(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
function dueIn(days: number, months = 0): string {
  const d = new Date();
  if (months > 0) d.setMonth(d.getMonth() + months);
  if (days > 0) d.setDate(d.getDate() + days);
  return localDateStr(d);
}

/**
 * TaskDetailModal (v2.7.2, 2026-06-11) — 칸반 카드 클릭 → 태스크 상세·편집 레이어팝업.
 * TaskCreateModal v2.6.1 동형 상세화: 섹션 그룹 + 제목 카운터 + 마감 빠른 칩 +
 * 푸터 규격(삭제=좌 danger ghost / 취소·저장=우). 태그는 patch API 부재로 읽기 전용 칩.
 * 변경분만 PATCH (dirty 필드 산출), 마감 지움은 dueDate:null 전송.
 */
export function TaskDetailModal({
  task, teamId = null, onClose, onSaved, onDeleted, onCustomFieldsChanged, isLeaving = false,
}: TaskDetailModalProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [status, setStatus] = useState<TaskStatus>(task.status as TaskStatus);
  const [priority, setPriority] = useState<TaskPriority>(task.priority as TaskPriority);
  const [dueDate, setDueDate] = useState(task.dueDate ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // v2.9.17 — 담당자: 팀원만 선택 가능. 무소속 프로젝트는 지정 불가(안내만).
  const [assignees, setAssignees] = useState<string[]>(task.assignees);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDto[]>([]);
  useEffect(() => {
    if (!teamId) { setTeamMembers([]); return; }
    let cancelled = false;
    void listTeamMembers(teamId)
      .then((members) => { if (!cancelled) setTeamMembers(members ?? []); })
      .catch(() => { /* 후보 로드 실패 — 읽기 표시만 유지 */ });
    return () => { cancelled = true; };
  }, [teamId]);
  const memberName = (userId: string) => teamMembers.find((m) => m.userId === userId)?.name ?? `#${userId}`;
  // 추가 정보의 MEMBER 필드 선택지 — 담당자 후보와 같은 팀원 목록을 그대로 쓴다.
  const cfMemberOptions = useMemo(
    () => teamMembers.map((m) => ({ userId: m.userId, name: m.name })),
    [teamMembers],
  );
  function toggleAssignee(userId: string) {
    setAssignees((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  }

  // v2.8 — 첨부 + 코멘트
  const me = useAuthStore((s) => s.user);
  // v2.9.14 — 수정은 작성자만, 비작성자는 코멘트(+다운로드)만. 칸반 드래그 상태변경은 무관(보드 경로).
  const canEdit = me !== null && String(me.id) === task.createdBy;
  // 담당자는 작성자가 아니어도 상태·완료일자(dueDate)를 변경할 수 있다 (그 외 필드는 작성자만).
  const isAssignee = me !== null && task.assignees.includes(String(me.id));
  const canEditStatusDue = canEdit || isAssignee;
  /**
   * 추가 정보 — 목록보기 표의 ＋ 로 만든 커스텀 필드 (2026-08-21).
   *
   * 정의(fields)는 프로젝트 것이고 값(values)은 이 태스크 것만 골라 담는다. 서버 응답은
   * 프로젝트 전체 태스크의 값을 주므로 taskId 로 거른다 — 표가 쓰는 API 그대로다.
   */
  const [cfFields, setCfFields] = useState<CustomFieldDto[]>([]);
  const [cfValues, setCfValues] = useState<Record<string, CustomFieldValue>>({});
  useEffect(() => {
    let cancelled = false;
    void listCustomFields(task.projectId)
      .then((res) => {
        if (cancelled) return;
        setCfFields(res.fields);
        const map: Record<string, CustomFieldValue> = {};
        for (const v of res.values) if (v.taskId === task.id) map[v.fieldId] = v.value;
        setCfValues(map);
      })
      .catch(() => { if (!cancelled) setCfFields([]); });
    return () => { cancelled = true; };
  }, [task.projectId, task.id]);

  /**
   * 값 저장 — 표의 셀과 같이 **고치는 즉시** 서버로 보낸다(저장 버튼을 기다리지 않는다).
   * 표의 saveField 와 같은 낙관적 갱신 + 실패 시 원복이며, 성공하면 표에 알린다.
   */
  async function saveCustomField(field: CustomFieldDto, next: CustomFieldValue) {
    const prev = cfValues[field.id] ?? null;
    setCfValues((v) => ({ ...v, [field.id]: next }));
    try {
      await setCustomFieldValue(task.id, field.id, next);
      onCustomFieldsChanged?.();
    } catch {
      setCfValues((v) => ({ ...v, [field.id]: prev }));
      void layerAlert(tTask('task.detail.extra_err'));
    }
  }

  const [attachments, setAttachments] = useState<TaskAttachmentDto[]>([]);
  const [comments, setComments] = useState<TaskCommentDto[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [collabBusy, setCollabBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [att, com] = await Promise.allSettled([
        listTaskAttachments(task.id),
        listTaskComments(task.id),
      ]);
      if (cancelled) return;
      // v2.9.17 — 비정형 응답(필드 누락) 방어: undefined 면 빈 배열 유지 (length 크래시 봉합).
      if (att.status === 'fulfilled') setAttachments(att.value ?? []);
      if (com.status === 'fulfilled') setComments(com.value ?? []);
    })();
    return () => { cancelled = true; };
  }, [task.id]);

  async function handleAttachFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setCollabBusy(true);
    try {
      for (const f of Array.from(files).slice(0, 20 - attachments.length)) {
        const up = await uploadChatAttachment(f);
        await addTaskAttachment(task.id, up.fileId);
      }
      setAttachments(await listTaskAttachments(task.id));
    } catch {
      void layerAlert(tTask('task.detail.attach_err'));
    } finally {
      setCollabBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDownload(a: TaskAttachmentDto) {
    try {
      const { downloadUrl } = await requestPresignedDownload(a.fileId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch {
      void layerAlert(tTask('task.detail.attach_err'));
    }
  }

  async function handleAttachDelete(a: TaskAttachmentDto) {
    setCollabBusy(true);
    try {
      await deleteTaskAttachment(task.id, a.id);
      setAttachments((prev) => prev.filter((x) => x.id !== a.id));
    } catch {
      void layerAlert(tTask('task.detail.attach_err'));
    } finally {
      setCollabBusy(false);
    }
  }

  async function handleCommentSend() {
    const content = commentInput.trim();
    if (!content) return;
    setCollabBusy(true);
    try {
      const created = await createTaskComment(task.id, content);
      setComments((prev) => [...prev, created]);
      setCommentInput('');
    } catch {
      void layerAlert(tTask('task.detail.comment_err'));
    } finally {
      setCollabBusy(false);
    }
  }

  async function handleCommentDelete(c: TaskCommentDto) {
    if (!(await layerConfirm(tTask('task.detail.comment_delete_confirm'), { danger: true }))) return;
    setCollabBusy(true);
    try {
      await deleteTaskComment(task.id, c.id);
      setComments((prev) => prev.filter((x) => x.id !== c.id));
    } catch {
      void layerAlert(tTask('task.detail.comment_err'));
    } finally {
      setCollabBusy(false);
    }
  }

  useEscapeClose(onClose, busy);

  const statusOptions = useMemo(
    () => TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS_META[s].label })),
    [],
  );
  const priorityOptions = useMemo(
    () => TASK_PRIORITIES.map((p) => ({ value: p, label: TASK_PRIORITY_META[p].label })),
    [],
  );

  const assigneesDirty =
    assignees.length !== task.assignees.length
    || assignees.some((id) => !task.assignees.includes(id));

  const dirty =
    title.trim() !== task.title
    || description.trim() !== (task.description ?? '')
    || status !== task.status
    || priority !== task.priority
    || (dueDate || '') !== (task.dueDate ?? '')
    || assigneesDirty;

  async function handleSave() {
    if (!title.trim() || !dirty) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await patchTask(task.id, {
        ...(title.trim() !== task.title ? { title: title.trim() } : {}),
        ...(description.trim() !== (task.description ?? '') ? { description: description.trim() } : {}),
        ...(status !== task.status ? { status } : {}),
        ...(priority !== task.priority ? { priority } : {}),
        ...((dueDate || '') !== (task.dueDate ?? '') ? { dueDate: dueDate || null } : {}),
        ...(assigneesDirty ? { assigneeUserIds: assignees } : {}),
      });
      onSaved(updated);
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      const msg = (err as any)?.response?.data?.message ?? (err as Error).message;
      setError(`${code ? `[${code}] ` : ''}${msg ?? tTask('task.create.err_save_fallback')}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!(await layerConfirm(tTask('task.detail.delete_confirm', { title: task.title }), { danger: true }))) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTask(task.id);
      onDeleted();
    } catch (err) {
      setError((err as any)?.response?.data?.message ?? tTask('task.detail.err_delete'));
      setBusy(false);
    }
  }

  const createdDate = task.createdAt ? task.createdAt.slice(0, 10) : '';

  return (
    <div
      className={`task-modal-backdrop${isLeaving ? ' task-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !busy && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tTask('task.detail.heading')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`task-modal task-modal--detail task-modal--strip${isLeaving ? ' task-modal--leaving' : ''}`}>
        {/* v2.8.1 — 프로젝트 컬러 스트립 (상단 카드 바 C안 동형) */}
        <span className="task-modal-strip" style={{ background: stripColor(task.projectId) }} aria-hidden="true" />
        <header className="task-modal-header">
          <h2 className="task-detail-heading">
            <TaskStatusIcon status={status} size={15} />
            {tTask('task.detail.heading')}
          </h2>
          <button type="button" className="task-modal-close" onClick={onClose} disabled={busy} aria-label={tTask('task.btn.close')} title={tTask('task.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <div className="task-modal-body">
          {!canEdit && (
            <div className="task-detail-readonly-banner" role="note">
              {isAssignee ? tTask('task.detail.assignee_edit_hint') : tTask('task.detail.readonly_hint')}
            </div>
          )}
          <section className="task-form-section" aria-label={tTask('task.create.section_basic')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--blue" aria-hidden="true"><FileText size={13} /></span>
              {tTask('task.create.section_basic')}
            </h3>
            <div className="task-modal-field">
              <div className="task-field-head">
                <label htmlFor="task-d-title">
                  {tTask('task.create.label_title')}
                  <em className="task-req" aria-hidden="true">*</em>
                </label>
                <span className="task-field-counter" aria-label={tTask('task.create.title_counter_aria')}>
                  {title.length}/200
                </span>
              </div>
              <input
                id="task-d-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                required
                aria-required="true"
                disabled={busy || !canEdit}
              />
            </div>
            <div className="task-modal-field">
              <label htmlFor="task-d-desc">{tTask('task.create.label_description')}</label>
              <AutoGrowTextarea
                id="task-d-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={tTask('task.create.desc_placeholder')}
                rows={3}
                maxLength={5000}
                disabled={busy || !canEdit}
              />
            </div>
          </section>

          <section className="task-form-section" aria-label={tTask('task.create.section_detail')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--violet" aria-hidden="true"><Settings size={13} /></span>
              {tTask('task.create.section_detail')}
            </h3>
            <div className="task-modal-field-row">
              <div className="task-modal-field">
                <span>{tTask('task.create.label_status')}</span>
                <Combobox
                  value={status}
                  onChange={(v) => setStatus(v as TaskStatus)}
                  options={statusOptions}
                  renderOption={(o) => (
                    <span className="task-status-option">
                      <TaskStatusIcon status={o.value as TaskStatus} size={12} />
                      {o.label}
                    </span>
                  )}
                  ariaLabel={tTask('task.create.label_status')}
                  disabled={busy || !canEditStatusDue}
                />
              </div>
              <div className="task-modal-field">
                <span>{tTask('task.create.label_priority')}</span>
                <Combobox
                  value={priority}
                  onChange={(v) => setPriority(v as TaskPriority)}
                  options={priorityOptions}
                  ariaLabel={tTask('task.create.label_priority')}
                  disabled={busy || !canEdit}
                />
              </div>
            </div>

            <div className="task-modal-field">
              <label htmlFor="task-d-due">{tTask('task.create.label_due')}</label>
              <div className="task-due-row">
                <input
                  id="task-d-due"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={busy || !canEditStatusDue}
                />
                <span className="task-due-chips" role="group" aria-label={tTask('task.create.due_quick_aria')}>
                  {([
                    { key: '1w', label: tTask('task.create.due_quick_1w'), value: dueIn(7) },
                    { key: '2w', label: tTask('task.create.due_quick_2w'), value: dueIn(14) },
                    { key: '1m', label: tTask('task.create.due_quick_1m'), value: dueIn(0, 1) },
                  ]).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`task-due-chip${dueDate === c.value ? ' task-due-chip--on' : ''}`}
                      onClick={() => setDueDate(c.value)}
                      disabled={busy || !canEditStatusDue}
                    >
                      {c.label}
                    </button>
                  ))}
                  {dueDate && (
                    <button type="button" className="task-due-chip task-due-chip--clear" onClick={() => setDueDate('')} disabled={busy || !canEditStatusDue}>
                      {tTask('task.create.due_clear')}
                    </button>
                  )}
                </span>
              </div>
            </div>

            {/* v2.9.17 — 담당자: 팀원만 선택 (무소속 프로젝트는 지정 불가 안내) */}
            <div className="task-modal-field">
              <span>{tTask('task.detail.assignee_label')}</span>
              {teamId === null ? (
                assignees.length > 0 ? (
                  <span className="task-assignee-chips" aria-label={tTask('task.detail.assignee_label')}>
                    {assignees.map((id) => (
                      <span key={id} className="task-assignee-chip task-assignee-chip--on task-assignee-chip--static">#{id}</span>
                    ))}
                  </span>
                ) : (
                  <span className="task-detail-hint">{tTask('task.detail.assignee_no_team')}</span>
                )
              ) : teamMembers.length === 0 ? (
                <span className="task-detail-hint">{tTask('task.detail.assignee_empty')}</span>
              ) : (
                <span className="task-assignee-chips" role="group" aria-label={tTask('task.detail.assignee_label')}>
                  {teamMembers.map((m) => {
                    const on = assignees.includes(m.userId);
                    return (
                      <button
                        key={m.userId}
                        type="button"
                        className={`task-assignee-chip${on ? ' task-assignee-chip--on' : ''}`}
                        onClick={() => toggleAssignee(m.userId)}
                        disabled={busy || !canEdit}
                        aria-pressed={on}
                      >
                        {m.avatarUrl ? (
                          <img className="task-assignee-avatar" src={m.avatarUrl} alt="" />
                        ) : (
                          <span className="task-assignee-avatar task-assignee-avatar--initial" aria-hidden="true">{m.name.slice(0, 1)}</span>
                        )}
                        {m.name}
                      </button>
                    );
                  })}
                  {/* 팀에서 빠진 기존 담당자 — 이름 미상(#id) 칩, 선택 해제만 가능 */}
                  {assignees.filter((id) => !teamMembers.some((m) => m.userId === id)).map((id) => (
                    <button
                      key={id}
                      type="button"
                      className="task-assignee-chip task-assignee-chip--on task-assignee-chip--gone"
                      onClick={() => toggleAssignee(id)}
                      disabled={busy || !canEdit}
                      aria-pressed="true"
                      title={tTask('task.detail.assignee_gone_tooltip')}
                    >
                      {memberName(id)}
                    </button>
                  ))}
                </span>
              )}
            </div>

            {task.tags.length > 0 && (
              <div className="task-modal-field">
                <span>{tTask('task.detail.tags_label')}</span>
                <span className="task-tag-preview" role="group" aria-label={tTask('task.detail.tags_label')}>
                  {task.tags.map((t) => (
                    <span key={t} className="task-tag-preview-chip">{t}</span>
                  ))}
                </span>
                <span className="task-detail-hint">{tTask('task.detail.tags_readonly_hint')}</span>
              </div>
            )}

            <div className="task-detail-meta">
              {createdDate && (
                <span className="task-detail-meta-item">
                  <Clock size={11} />
                  {tTask('task.detail.created_at', { date: createdDate })}
                </span>
              )}
              {task.assignees.length > 0 && (
                <span className="task-detail-meta-item">
                  {tTask('task.detail.assignees', { n: task.assignees.length })}
                </span>
              )}
            </div>
          </section>

          {/* ── v2.8 첨부 파일 ── */}
          <section className="task-form-section" aria-label={tTask('task.detail.attachments')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--amber" aria-hidden="true"><Paperclip size={13} /></span>
              {tTask('task.detail.attachments')} ({attachments.length})
            </h3>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => void handleAttachFiles(e.target.files)}
            />
            {attachments.length === 0 ? (
              <span className="task-detail-hint">{tTask('task.detail.attach_empty')}</span>
            ) : (
              <ul className="task-attach-list">
                {attachments.map((a) => (
                  <li key={a.id} className="task-attach-item">
                    <AttachmentTypeIcon mimeType={a.mimeType} name={a.name} />
                    <span className="task-attach-name" title={a.name}>{a.name}</span>
                    <span className="task-attach-size">{Math.max(1, Math.round(a.sizeBytes / 1024))}KB</span>
                    <button
                      type="button"
                      className="task-attach-action"
                      onClick={() => void handleDownload(a)}
                      aria-label={`${a.name} ${tTask('task.detail.download')}`}
                    >
                      <Download size={12} />
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className="task-attach-remove"
                        onClick={() => void handleAttachDelete(a)}
                        disabled={collabBusy}
                      >
                        {tTask('task.detail.attach_delete')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canEdit && (
              <div className="task-attach-row">
                <button
                  type="button"
                  className="task-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={collabBusy || attachments.length >= 20}
                >
                  <Paperclip size={13} />
                  {tTask('task.detail.attach_add')}
                </button>
                {collabBusy && <span className="task-attach-uploading">…</span>}
              </div>
            )}
          </section>

          {/*
            ── 추가 정보 (2026-08-21) ──
            목록보기 표 오른쪽 끝 ＋ 로 만든 **커스텀 필드**를 상세에서도 채우는 자리다.
            표의 셀과 **같은 편집기**(FieldCell)를 쓴다 — 멤버는 팝오버, 상태·선택은 목록,
            체크박스·숫자·날짜·텍스트는 그대로. 그래서 두 화면의 조작이 달라질 수 없다.
            값은 저장 버튼과 무관하게 고치는 즉시 반영된다(표의 셀과 같은 규칙).
            필드가 하나도 없으면 섹션 자체를 그리지 않는다 — 빈 상자를 남기지 않으려는 것.
          */}
          {cfFields.length > 0 && (
            <section className="task-form-section" aria-label={tTask('task.detail.extra')}>
              <h3 className="task-form-section-head">
                <span className="task-section-icon task-section-icon--violet" aria-hidden="true"><Columns size={13} /></span>
                {tTask('task.detail.extra')} ({cfFields.length})
              </h3>
              <p className="task-detail-hint">{tTask('task.detail.extra_hint')}</p>
              <ul className="task-extra-list">
                {cfFields.map((f) => (
                  <li key={f.id} className="task-extra-row">
                    <span className="task-extra-label" title={f.name}>{f.name}</span>
                    <span className="task-extra-value">
                      <FieldCell
                        field={f}
                        value={cfValues[f.id] ?? null}
                        members={cfMemberOptions}
                        disabled={busy || !canEdit}
                        onChange={(next) => void saveCustomField(f, next)}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── v2.8 코멘트 ── */}
          <section className="task-form-section" aria-label={tTask('task.detail.comments')}>
            <h3 className="task-form-section-head">
              <span className="task-section-icon task-section-icon--green" aria-hidden="true"><MessageSquare size={13} /></span>
              {tTask('task.detail.comments')} ({comments.length})
            </h3>
            {comments.length === 0 ? (
              <span className="task-detail-hint">{tTask('task.detail.comment_empty')}</span>
            ) : (
              <ul className="task-comment-list">
                {comments.map((c) => {
                  const mine = me !== null && String(me.id) === c.authorId;
                  return (
                    <li key={c.id} className="task-comment">
                      {c.authorAvatarUrl ? (
                        <img className="task-comment-avatar" src={c.authorAvatarUrl} alt="" />
                      ) : (
                        <span className="task-comment-avatar task-comment-avatar--initial" aria-hidden="true">
                          {c.authorName.slice(0, 1)}
                        </span>
                      )}
                      <span className="task-comment-body">
                        <span className="task-comment-head">
                          <b>{c.authorName}</b>
                          <time>{c.createdAt.replace('T', ' ').slice(0, 16)}</time>
                          {mine && (
                            <button
                              type="button"
                              className="task-comment-del"
                              onClick={() => void handleCommentDelete(c)}
                              disabled={collabBusy}
                              aria-label={tTask('task.detail.comment_delete')}
                              title={tTask('task.detail.comment_delete')}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </span>
                        <span className="task-comment-content">{c.content}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="task-comment-input-row">
              <input
                type="text"
                className="task-comment-input"
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleCommentSend(); } }}
                placeholder={tTask('task.detail.comment_placeholder')}
                maxLength={2000}
                disabled={collabBusy}
              />
              <button
                type="button"
                className="task-comment-send"
                onClick={() => void handleCommentSend()}
                disabled={collabBusy || !commentInput.trim()}
                aria-label={tTask('task.detail.comment_send')} title={tTask('task.detail.comment_send')}
              >
                <Send size={14} />
              </button>
            </div>
          </section>

          {error && <div className="task-modal-error" role="alert">{error}</div>}
        </div>

        {/* 하단 버튼 — 스크롤 본문 밖(모달 맨 하단 고정) */}
        <footer className="task-modal-footer task-modal-footer--split">
          {canEdit && (
            <button type="button" className="task-btn-ghost task-btn-ghost--danger" onClick={() => void handleDelete()} disabled={busy}>
              {tTask('task.detail.btn_delete')}
            </button>
          )}
          <span className="task-detail-foot-btns">
            <button type="button" className="task-btn-ghost" onClick={onClose} disabled={busy}>
              {tTask('task.btn.cancel')}
            </button>
            {canEditStatusDue && (
              <button
                type="button"
                className="task-modal-submit task-btn-primary"
                onClick={() => void handleSave()}
                disabled={busy || !dirty || !title.trim()}
              >
                {busy ? tTask('task.btn.saving') : tTask('task.btn.save')}
              </button>
            )}
          </span>
        </footer>
      </div>
    </div>
  );
}
